import React, { useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useDispatch } from '@tmapy/redux';
import { BtnBar, CtaBtn, SecondaryBtn } from '@tmapy/style-guide';

import './ServiceOrder.scss';

import { MainContent } from '../../app/components/MainContent';
import { OrderAttachmentsSection } from '../../app/components/OrderAttachmentsSection';
import type { OrderAttachmentItem } from '../../app/components/OrderAttachmentsSection';
import { PageTitle } from '../../app/components/PageTitle';
import { QUERY_STATE } from '../../constants';
import { toBtnQueryState, useAuth, useHistoryAction, useSelector } from '../../hooks';
import { serviceOrdersAttachmentsCreate } from '../../openapi/operations/serviceOrdersAttachmentsCreate';
import { serviceOrdersCreate } from '../../openapi/operations/serviceOrdersCreate';
import { serviceOrdersListOperation } from '../../openapi/operations/serviceOrdersList';
import type { ServiceOrderEditRequest } from '../../openapi/types/ServiceOrderEdit';
import { actionResourceInvalidate } from '../../redux/resource/actions';
import { routeToUrl } from '../../routes';
import type { ServerErrors } from '../../types';
import { stripEmptyFilters } from '../../utils/queryArgs';

import type { ServiceOrderFormHandle } from './ServiceOrderForm';
import { ServiceOrderForm } from './ServiceOrderForm';
import { ServiceOrderSaveAlerts } from './ServiceOrderSaveAlerts';
import { serviceOrderBtnMessages, serviceOrderFormMessages } from './serviceOrderMessages';

/** Předává detailu soubory, které se po založení žádosti nepodařilo nahrát. */
export type NewServiceOrderNavigationState = { failedAttachments: string[] };

const NO_ATTACHMENTS: OrderAttachmentItem[] = [];

/**
 * Zakládá rozpracovanou objednávku. Cenu počítá server, takže se po vytvoření
 * přechází na detail, kde ji pracovnice zkontroluje a objednávku potvrdí.
 */
export const NewServiceOrder: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useAuth();
  const filters = useSelector((state) => state.filter.serviceOrderList);

  const [submitQueryState, setSubmitQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [serverErrors, setServerErrors] = useState<ServerErrors | null>(null);
  const formRef = useRef<ServiceOrderFormHandle>(null);

  // Přílohu jde nahrát až k existující žádosti a backend ji bez položek a odběratele nezaloží,
  // proto se vybrané soubory jen zařadí a nahrají se hned po vytvoření žádosti.
  const pendingFiles = useRef(new Map<number, File>());
  const nextPendingId = useRef(0);

  const routeToList = useHistoryAction('vydejServiceOrderList');

  const handleCreateBtnClick = useCallback(() => formRef.current?.submit(), []);

  const handleAttachmentQueue = useCallback(async (file: File) => {
    const id = nextPendingId.current++;
    pendingFiles.current.set(id, file);
    return { ok: true as const, data: { id, name: file.name, isPending: true } };
  }, []);

  const handleAttachmentUnqueue = useCallback(async (id: OrderAttachmentItem['id']) => {
    pendingFiles.current.delete(Number(id));
    return { ok: true };
  }, []);

  const handleSubmit = useCallback(
    async (values: ServiceOrderEditRequest) => {
      try {
        setSubmitQueryState(QUERY_STATE.WAITING);
        setServerErrors(null);
        const response = await serviceOrdersCreate({ request: values });
        if (response.ok && response.data) {
          const { id } = response.data;
          const failedAttachments: string[] = [];
          for (const file of pendingFiles.current.values()) {
            const request = new FormData();
            request.set('file', file);
            const upload = await serviceOrdersAttachmentsCreate({ query: { id }, request });
            if (!upload.ok) failedAttachments.push(file.name);
          }
          setSubmitQueryState(QUERY_STATE.SUCCESS);
          const paths = new Set([
            serviceOrdersListOperation({}).path,
            serviceOrdersListOperation(stripEmptyFilters(filters)).path,
          ]);
          paths.forEach((path) => dispatch(actionResourceInvalidate(path)));
          const state: NewServiceOrderNavigationState | undefined = failedAttachments.length
            ? { failedAttachments }
            : undefined;
          navigate(routeToUrl.vydejServiceOrderDetail({ id }), { state });
        } else {
          setServerErrors(response.error?.data ?? null);
          setSubmitQueryState(QUERY_STATE.FAIL);
        }
      } catch (e) {
        setSubmitQueryState(QUERY_STATE.FAIL);
      }
    },
    [dispatch, filters, navigate],
  );

  const isSaving = submitQueryState === QUERY_STATE.WAITING;
  const canAttach = auth.hasPermission('ServiceOrders', 'upload_attachment', null) && !isSaving;

  return (
    <MainContent width='full'>
      <article className='sg-u-vs-4'>
        <div className='dmx-serviceOrder--head sg-u-vs-2'>
          <div className='sg-a-d-f sg-a-g-2 sg-a-ai-c sg-a-jc-sb sg-a-fw-w'>
            <PageTitle>
              <FormattedMessage {...serviceOrderFormMessages.newTitle} />
            </PageTitle>
            <BtnBar>
              <CtaBtn queryState={toBtnQueryState(submitQueryState)} onClick={handleCreateBtnClick}>
                <FormattedMessage {...serviceOrderBtnMessages.create} />
              </CtaBtn>
              <SecondaryBtn isDanger onClick={routeToList}>
                <FormattedMessage {...serviceOrderBtnMessages.cancel} />
              </SecondaryBtn>
            </BtnBar>
          </div>
          <ServiceOrderSaveAlerts queryState={submitQueryState} serverErrors={serverErrors} />
        </div>
        <div className='dmx-serviceOrder--cols'>
          <div className='sg-u-vs-4'>
            <ServiceOrderForm
              ref={formRef}
              data={null}
              submitQueryState={submitQueryState}
              serverErrors={serverErrors}
              onSubmit={handleSubmit}
            />
          </div>
          <div className='sg-u-vs-4'>
            <OrderAttachmentsSection
              resourceUrl={null}
              attachments={NO_ATTACHMENTS}
              onUpload={canAttach ? handleAttachmentQueue : undefined}
              onDelete={canAttach ? handleAttachmentUnqueue : undefined}
            />
          </div>
        </div>
      </article>
    </MainContent>
  );
};
